import clsx from 'clsx';
import React from 'react';
import { ButtonContext, useContextProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { assertNever } from '@gitbook/util-ts';

import { Badge } from '../Badge';
import { Tooltip } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';
import { AtomButton, AtomLink, type AtomLinkProps } from './AtomButton';
import styles from './Button.module.css';
import type { ButtonProps, ButtonSize } from './Button.types';
import { useLoadableButton } from './useLoadableButton';

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    function ButtonInnerWithRef(props, ref) {
        [props, ref] = useContextProps(props, ref, ButtonContext);
        const {
            active,
            badge,
            kind = 'primary',
            iconOnly,
            inverted,
            size = 'medium',
            testID,
            title,
            label,
            labelStyle,
            leadingIcon,
            loadable,
            rounded,
            trailingIcon,
            tooltip,
            dot,
            ...buttonProps
        } = useLoadableButton(props);

        const hasIcon = Boolean(leadingIcon || trailingIcon);

        /**
         * If the button is an iconButton with no title use the label as a tooltip
         */
        const tooltipLabel = title || (hasIcon && iconOnly === true ? label : undefined);
        const tooltipProps = tooltip ?? {
            label: tooltipLabel,
            disabled: !tooltipLabel,
        };

        const buttonStyles = clsx(
            dot ? 'relative' : null,
            styles.button,
            [styles[kind]],
            kind !== 'close' && [styles[size]],
            rounded || kind === 'pill' ? 'rounded-full' : 'rounded-md',
            hasIcon && iconOnly && getIconOnlyClassName(iconOnly),
            {
                [styles.active]: active,
                [styles.disabled]: buttonProps.disabled,
                [styles.inverted]: inverted,
            },
            buttonProps.className
        );

        const style = {
            ...buttonProps.style,
            '--icon-size': `${getIconSize({ size, iconOnly })}px`,
        };

        const labelSlot = label ? (
            <ButtonLabel hasIcon={hasIcon} iconOnly={iconOnly} size={size} labelStyle={labelStyle}>
                {label}
            </ButtonLabel>
        ) : null;

        const leadingIconSlot = leadingIcon ? (
            <ButtonIcon icon={leadingIcon} size={size} iconOnly={iconOnly} />
        ) : null;

        const trailingIconSlot = trailingIcon ? (
            <ButtonIcon icon={trailingIcon} size={size} iconOnly={iconOnly} />
        ) : null;

        const badgeSlot = badge ? (
            <Badge {...badge} size={['xsmall', 'small'].includes(size) ? 'xsmall' : 'small'} />
        ) : null;

        const dotSlot = dot ? (
            <Icon.Dot className="absolute top-0 right-0 size-3 text-upgrade" />
        ) : null;

        return (
            <Tooltip {...tooltipProps}>
                {!buttonProps.disabled && buttonProps.href ? (
                    <AtomLink
                        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                        {...(buttonProps as AtomLinkProps)}
                        data-testid={testID}
                        className={buttonStyles}
                        style={style}
                    >
                        {leadingIconSlot}
                        {labelSlot}
                        {badgeSlot}
                        {trailingIconSlot}
                        {dotSlot}
                    </AtomLink>
                ) : (
                    <AtomButton
                        ref={ref}
                        {...buttonProps}
                        data-testid={testID}
                        className={buttonStyles}
                        style={style}
                    >
                        {leadingIconSlot}
                        {labelSlot}
                        {badgeSlot}
                        {trailingIconSlot}
                        {dotSlot}
                    </AtomButton>
                )}
            </Tooltip>
        );
    }
);

export function ButtonLabel(props: {
    children: React.ReactNode;
    hasIcon?: boolean;
    iconOnly?: ButtonProps['iconOnly'];
    labelStyle?: string;
    size?: ButtonSize;
}) {
    const { children, hasIcon, iconOnly, size = 'medium', labelStyle } = props;

    return (
        <span
            className={clsx(
                'max-w-full truncate',
                hasIcon && iconOnly && getIconOnlyTextClassName(iconOnly),
                ['xsmall', 'small'].includes(size) ? 'text-ui-small' : 'text-ui-action',
                labelStyle
            )}
        >
            {children}
        </span>
    );
}

function getIconOnlyClassName(iconOnly: ButtonProps['iconOnly']) {
    switch (iconOnly) {
        case 'max-md':
            return 'max-md:px-[calc((var(--button-size)-var(--icon-size))/2-var(--button-border-width))]';
        case 'max-lg':
            return 'max-lg:px-[calc((var(--button-size)-var(--icon-size))/2-var(--button-border-width))]';
        case 'max-xl':
            return 'max-xl:px-[calc((var(--button-size)-var(--icon-size))/2-var(--button-border-width))]';
        case true:
            return 'px-[calc((var(--button-size)-var(--icon-size))/2-var(--button-border-width))]';
        case undefined:
        case false:
            return '';
        default:
            assertNever(iconOnly);
    }
}

function getIconOnlyTextClassName(iconOnly: ButtonProps['iconOnly']) {
    switch (iconOnly) {
        case 'max-md':
            return 'max-md:sr-only';
        case 'max-lg':
            return 'max-lg:sr-only';
        case 'max-xl':
            return 'max-xl:sr-only';
        case true:
            return 'sr-only';
        case undefined:
        case false:
            return '';
        default:
            assertNever(iconOnly);
    }
}

export function ButtonIcon(props: {
    className?: string;
    icon: IconDefinition;
    size?: ButtonSize;
    iconOnly?: ButtonProps['iconOnly'];
}) {
    const { className, icon, size = 'medium', iconOnly = false } = props;
    return renderIcon(icon, {
        size: getIconSize({ size, iconOnly }),
        className: clsx('shrink-0', className),
    });
}

export function getIconSize({
    size,
    iconOnly,
}: {
    size: ButtonSize;
    iconOnly?: ButtonProps['iconOnly'];
}) {
    switch (size) {
        case 'xsmall':
            return 12;
        case 'small':
            return iconOnly ? 14 : 12;
        case 'medium':
            return 16;
        case 'large':
            return 20;
        case 'xlarge':
            return 24;
        default:
            return 16;
    }
}
